import { isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import React from "react";

import { faEllipsisH } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Chip, DropDownMenu, type DropDownMenuOption, Icon, type PYColumnDef, Text } from "@performyard/py-components";
import type { CompensationCycleResponse } from "@performyard/service-contracts";

const displayStatusColorMap: Record<
	CompensationCycleResponse["displayStatus"],
	"green" | "neutral" | "yellow" | "red" | "primary"
> = {
	Completed: "green",
	Cancelled: "neutral",
	Scheduled: "yellow",
	Overdue: "red",
	"In Progress": "primary",
};

const StatusRenderer = (params: { data?: CompensationCycleResponse }) => {
	if (!params.data) return null;
	const { displayStatus } = params.data;
	return <Chip label={displayStatus} color={displayStatusColorMap[displayStatus]} size={24} />;
};

const ProgressRenderer = (params: { data?: CompensationCycleResponse }) => {
	if (!params.data) return null;
	const { approvedCount, employeeCount } = params.data.budgetSummary;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3">
				{approvedCount}/{employeeCount}
			</Text>
		</div>
	);
};

const DueDateRenderer = (params: { data?: CompensationCycleResponse }) => {
	if (!params.data?.dueDate) return null;
	const { dueDate, timezone } = params.data;
	const tz = timezone || "America/Eastern";
	try {
		const date = parseISO(dueDate);
		if (!isValid(date)) return null;
		return (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular">
					{formatInTimeZone(date, tz, "MMM d, yyyy")}
				</Text>
			</div>
		);
	} catch {
		return null;
	}
};

const createActionsRenderer = (canManage: boolean, onDelete: (cycle: CompensationCycleResponse) => void) => {
	const ActionsRenderer = (params: { data?: CompensationCycleResponse }) => {
		if (!params.data || !canManage) return null;
		const cycle = params.data;
		const options: DropDownMenuOption[] = [{ key: "delete", label: "Delete", onClick: () => onDelete(cycle) }];
		return (
			<DropDownMenu options={options} placement="bottom-end" className="group flex h-full items-center">
				<Icon
					icon={faEllipsisH}
					size={24}
					color="neutral-7"
					dangerouslySetClassName="transition-colors group-hover:text-neutral-9"
				/>
			</DropDownMenu>
		);
	};
	return ActionsRenderer;
};

export const getColumnDefs = (
	canManage: boolean,
	ActionsRenderer: ReturnType<typeof createActionsRenderer>
): PYColumnDef<CompensationCycleResponse>[] => [
	{
		field: "name",
		headerName: "Cycle",
		sortable: true,
		type: "link",
		valueGetter: (params) => {
			if (!params.data) return null;
			return { href: `/compensation/cycles/${params.data._id}`, name: params.data.name };
		},
		width: canManage ? 310 : 370,
	},
	{
		field: "created_by_name",
		headerName: "Creator",
		sortable: true,
		type: "employee",
		valueGetter: (params) => {
			if (!params.data) return null;
			return { _id: params.data.createdBy, name: params.data.createdByName ?? "" };
		},
		width: 180,
	},
	{
		field: "createdAt",
		headerName: "Date Created",
		sortable: true,
		type: "date",
		cellRendererParams: { dateFormat: "MMM d, yyyy" },
		width: 152,
	},
	{
		field: "startDate",
		headerName: "Start Date",
		sortable: true,
		type: "date",
		cellRendererParams: { dateFormat: "MMM d, yyyy" },
		width: 152,
	},
	{
		field: "dueDate",
		headerName: "Due Date",
		sortable: true,
		cellRenderer: DueDateRenderer,
		width: 152,
	},
	{
		field: "displayStatus",
		headerName: "Status",
		sortable: true,
		cellRenderer: StatusRenderer,
		width: 152,
	},
	{
		colId: "progress",
		headerName: "Progress",
		sortable: false,
		cellRenderer: ProgressRenderer,
		width: 152,
	},
	...(canManage
		? [
				{
					colId: "actions",
					headerName: "",
					sortable: false,
					cellRenderer: ActionsRenderer,
					width: 60,
					maxWidth: 60,
					cellStyle: {
						display: "flex",
						justifyContent: "flex-end",
						alignItems: "center",
					},
				} as PYColumnDef<CompensationCycleResponse>,
			]
		: []),
];

export { createActionsRenderer };
